import React, { type PropsWithChildren } from 'react';
import { useUpdates } from 'expo-updates';

import { theme } from '@sg/garnish';
import { useIsLoadingOta } from '@sg/ota-installer';

import { OrderLaunchDarklyProvider, useLaunchDarkly } from '@order/LaunchDarkly';

import { LoadingAnimation, SessionError } from '../components';
import {
  useCustomerData,
  useHasLoggedOut,
  useIsFirstAuthAttempt,
  useIsFetchingSession,
  useIsLoggingIn,
  useSessionError,
} from '../context/AuthMachine';
import { EssentialMachinesProvider, useEssentialMachines } from '../context/EssentialMachines';
import { useAppFonts, useLaunchDarklyIntegration, useStartOtaObserver } from '../hooks';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Owns the launch-time gate that handles OTA install flow, mounts the
 * LaunchDarkly client and the auth/OTA machines. It waits for the auth state to
 * settle so we can identify LaunchDarkly with the actual customer (avoiding a
 * wasteful anonymous-then-customer re-identify), then keeps app render blocked
 * until the required feature flag/s resolve/s — either to install an OTA on a quiet UI
 * tree, or to skip and let the rest of the app render.
 */
export const LaunchGate = (props: PropsWithChildren) => (
  <OrderLaunchDarklyProvider>
    <EssentialMachinesProvider>
      <LaunchGateRenderer>{props.children}</LaunchGateRenderer>
    </EssentialMachinesProvider>
  </OrderLaunchDarklyProvider>
);

// ─── Components ──────────────────────────────────────────────────────────────

/**
 * First leg of the gate: wait for fonts + session + auth settle. We only mount
 * the LaunchDarkly identify path once the auth machine has resolved to a
 * stable state (signed in or guest), so the SDK doesn't fire two back-to-back
 * identifies (anonymous, then customer).
 */
const LaunchGateRenderer = (props: PropsWithChildren) => {
  const { children } = props;

  const appFontsLoaded = useAppFonts();
  const isFetchingSession = useIsFetchingSession();
  const isLoggingIn = useIsLoggingIn();
  const isFirstAuthAttempt = useIsFirstAuthAttempt();
  const sessionError = useSessionError();

  // ─────────────────────────────────────────────────────────────────────

  const isFirstLoginInFlight = isFirstAuthAttempt && isLoggingIn;
  const isAuthResolved = !isFetchingSession && !isFirstLoginInFlight;
  const isAuthGateLoading = !appFontsLoaded || !isAuthResolved;

  // ─────────────────────────────────────────────────────────────────────

  if (sessionError) {
    return <SessionError error={sessionError} />;
  }

  if (isAuthGateLoading) {
    return <LoadingAnimation withNavbarOffset backgroundColor={theme.colors.APP_BACKGROUND} />;
  }

  return <LaunchGatePostAuth>{children}</LaunchGatePostAuth>;
};

/**
 * Second leg of the gate: only mounted once auth is settled. Initializes the
 * LaunchDarkly customer targeting context and dispatches the OTA feature flag
 * value to the OTA machine, then renders children once the OTA machine opens
 * the gate.
 */
const LaunchGatePostAuth = (props: PropsWithChildren) => {
  const { children } = props;

  const { otaMachineRef } = useEssentialMachines();
  const { isLaunchDarklyReady, isLaunchDarklyTargetingContextSet } = useLaunchDarkly();

  const { isRestarting } = useUpdates();
  const isLoadingOta = useIsLoadingOta({ actorRef: otaMachineRef });

  const customer = useCustomerData();
  const isLoggingIn = useIsLoggingIn();
  const hasLoggedOut = useHasLoggedOut();

  // ─── Flags ───────────────────────────────────────────────────────────

  const isLoading =
    !isLaunchDarklyReady || !isLaunchDarklyTargetingContextSet || isLoadingOta || isRestarting;

  // ─── Effects ─────────────────────────────────────────────────────────

  useLaunchDarklyIntegration({
    customer: customer ?? LD_CUSTOMER_FALLBACK,
    isLoggingIn,
    hasLoggedOut,
  });

  useStartOtaObserver();

  // ─────────────────────────────────────────────────────────────────────

  if (isLoading) {
    return <LoadingAnimation withNavbarOffset backgroundColor={theme.colors.APP_BACKGROUND} />;
  }

  return <>{children}</>;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const LD_CUSTOMER_FALLBACK = {
  id: undefined,
  email: undefined,
};
